// Sync in React: one engine per signed-in account (SyncProvider, inside the signed-in shell), the
// status for the indicator, and live lists of each collection that refresh when this tab, another
// tab or a pull changes them.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from "react";
import { SyncEngine, type SyncStatus } from "./engine";
import { hasIndexedDb, idbStore } from "./idb";
import { liveEntities, readMeta, patchMeta } from "./ops";
import { forgetLocalReflections, localReflectionsToMigrate, reflectionToNote } from "./reflections";
import { memoryStore } from "./store";
import type { Collection, LocalEntity } from "./types";

const SyncContext = createContext<SyncEngine | null>(null);

/** Engines by account, so a remount (StrictMode, navigation) reuses the running one. */
const engines = new Map<string, SyncEngine>();

export function engineFor(account: string, baseUrl: string | null): SyncEngine {
  let e = engines.get(account);
  // Sync switched on or off (GET /v1/config): the same store, with or without the network.
  if (e && e.baseUrl !== baseUrl) {
    e.stop();
    e = new SyncEngine({ store: e.store, account, baseUrl });
    engines.set(account, e);
    e.start();
  }
  if (!e) {
    e = new SyncEngine({ store: hasIndexedDb() ? idbStore(account) : memoryStore(), account, baseUrl });
    engines.set(account, e);
    e.start();
    void migrateReflections(e);
  }
  return e;
}

/** Signing out: push what's waiting (briefly), stop, and delete this browser's copy of the account's notes. */
export async function closeSync(account: string | null | undefined) {
  if (!account) return;
  const e = engines.get(account);
  if (!e) return;
  engines.delete(account);
  await Promise.race([e.syncNow(), new Promise((r) => setTimeout(r, 3000))]).catch(() => undefined);
  e.stop();
  await e.store.destroy();
}

/** Reflections kept in localStorage before sync become notes, once per account. */
async function migrateReflections(e: SyncEngine) {
  try {
    const meta = await readMeta(e.store);
    if (meta.reflectionsMigrated) return;
    const old = localReflectionsToMigrate();
    if (old.length) await e.mutate(old.map((r) => reflectionToNote(r)));
    await patchMeta(e.store, { reflectionsMigrated: true });
    forgetLocalReflections();
  } catch {
    // Tried again next time.
  }
}

export function SyncProvider({ account, baseUrl, children }: { account: string | null; baseUrl: string | null; children: ReactNode }) {
  const engine = useMemo(() => (account ? engineFor(account, baseUrl) : null), [account, baseUrl]);
  return <SyncContext.Provider value={engine}>{children}</SyncContext.Provider>;
}

export const useSyncEngine = () => useContext(SyncContext);

const LOCAL: SyncStatus = { pending: 0, network: "local", lastSyncedAt: null, message: null };

export function useSyncStatus(): SyncStatus {
  const e = useSyncEngine();
  return useSyncExternalStore(
    (cb) => (e ? e.onStatus(cb) : () => undefined),
    () => (e ? e.getStatus() : LOCAL),
  );
}

/** "Saved", "Saving…" or "Offline, will sync": what the indicator says. */
export function statusLabel(s: SyncStatus): { label: string; detail: string | null; tone: "ok" | "busy" | "warn" } {
  if (s.network === "local") return { label: "Saved on this device", detail: null, tone: "ok" };
  if (s.network === "offline") return { label: "Offline, will sync", detail: s.pending ? `${s.pending} ${s.pending === 1 ? "change" : "changes"} waiting` : null, tone: "warn" };
  if (s.network === "signed-out") return { label: "Not syncing", detail: s.message, tone: "warn" };
  if (s.network === "paused") return { label: s.pending ? "Saved here, will sync" : "Saved", detail: s.message, tone: "warn" };
  if (s.network === "error") return { label: s.pending ? "Saved here, will sync" : "Saved", detail: s.message, tone: s.message ? "warn" : "ok" };
  if (s.pending > 0 || s.network === "syncing") return { label: "Saving…", detail: null, tone: "busy" };
  return { label: "Saved", detail: null, tone: "ok" };
}

/** Live entities of one collection, newest first; refreshed whenever anything changes them. */
export function useCollection(collection: Collection) {
  const e = useSyncEngine();
  const qc = useQueryClient();
  const key = useMemo(() => ["sync", e?.account ?? "none", collection] as const, [e, collection]);
  useEffect(() => {
    if (!e) return;
    return e.onChange((c) => {
      if (c.has(collection)) void qc.invalidateQueries({ queryKey: key });
    });
  }, [e, qc, key, collection]);
  return useQuery<LocalEntity[]>({
    queryKey: key,
    queryFn: () => (e ? liveEntities(e.store, collection) : Promise.resolve([])),
    staleTime: Infinity,
    enabled: !!e,
  });
}

/** Re-render every `ms` (relative times in the indicator). */
export function useNow(ms = 30_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}
